// Wysiwyg.jsx
import React, { useMemo } from "react";

/* ───────── Browser-only lazy requires ───────── */
let ReactQuill = null;
let Quill = null;
let BetterTable = null;

if (typeof window !== "undefined") {
  /* Core editor */
  ReactQuill = require("react-quill");
  Quill       = require("quill");
  require("react-quill/dist/quill.snow.css");

  /* quill-better-table */
  try {
    BetterTable = require("quill-better-table").default;          // ESM default export
    require("quill-better-table/dist/quill-better-table.min.css"); // plugin CSS

    /* Register the module & formats exactly once */
    if (!Quill.imports["modules/better-table"]) {
      Quill.register(
        {
          "modules/better-table": BetterTable,
          "formats/table":        BetterTable.TableCell,
          "formats/table-row":    BetterTable.TableRow,
          "formats/table-cell":   BetterTable.TableCell,
        },
        true
      );
    }
  } catch (err) {
    console.warn(
      "Warning: quill-better-table not available – tables will render as static HTML only.",
      err
    );
    BetterTable = null;
  }
}

/* ───────────────────────────────────────────────
 *  <Wysiwyg />
 * ───────────────────────────────────────────── */
export default function Wysiwyg({
  children,
  name,
  placeholder,
  className,
  value,
  id,
  onChange,
  toolbarOptions = [],
  enableTables   = true,
}) {
  /* React-Quill can take HTML or Delta – mirror your old logic */
  const safeValue =
    typeof children === "object" || typeof children === "function"
      ? JSON.stringify(children)
      : children ?? value ?? "";

  /* ---------- Toolbar ---------- */
  const baseToolbar = [
    ["bold", "italic", "underline", "strike"],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ direction: "rtl" }],
    [{ align: [] }],
    ["link", "image", "video"],
    ["clean"],
  ];

  const toolbar =
    toolbarOptions.length > 0
      ? toolbarOptions
      : enableTables && BetterTable
        ? [...baseToolbar, [{ table: "TD" }]]
        : baseToolbar;

  /* ---------- Modules ---------- */
  const modules = useMemo(() => {
    const m = {
      toolbar,
      clipboard: { matchVisual: false },
    };

    if (enableTables && BetterTable) {
      m["better-table"] = {
        operationMenu: {
          items: {
            unmergeCells: { text: "Unmerge cells" },
          },
          color: {
            colors: ["green", "red", "yellow", "blue"],
            text: "Background Colors:",
          },
        },
      };
      m.keyboard = {
        bindings: BetterTable.keyboardBindings, // Tab/arrow navigation
      };
    }
    return m;
  }, [toolbar, enableTables]);

  /* ---------- Formats ---------- */
  const formats = [
    "header", "font", "size",
    "bold", "italic", "underline", "strike", "blockquote",
    "list", "bullet", "indent",
    "link", "image", "video",
    "align", "direction",
    "script", "color", "background",
    ...(enableTables && BetterTable
      ? ["table", "table-row", "table-cell"]
      : []),
  ];

  /* ---------- Render ---------- */
  if (ReactQuill) {
    return (
      <div>
        <ReactQuill
          theme="snow"
          modules={modules}
          formats={formats}
          style={{ backgroundColor: "white", color: "black" }}
          name={name}
          placeholder={placeholder || ""}
          className={className}
          id={id}
          value={safeValue}
          onChange={onChange}
        />

        {/* Plain <style> tag avoids “non-boolean attribute `global`” warning */}
        <style>{`
          .ql-editor table {
            border-collapse: collapse;
            width: 100%;
            margin: 10px 0;
          }
          .ql-editor table th,
          .ql-editor table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            vertical-align: top;
          }
          .ql-editor table th {
            background: #f2f2f2;
            font-weight: 700;
          }
          .ql-editor table tr:nth-child(even) {
            background: #f9f9f9;
          }
          /* quill-better-table wrapper for horizontal scroll */
          .ql-better-table-wrapper {
            overflow-x: auto;
          }
        `}</style>
      </div>
    );
  }

  /* ---------- SSR / no-JS fallback ---------- */
  return (
    <textarea
      name={name}
      placeholder={placeholder || ""}
      className={className}
      id={id}
      value={safeValue}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", minHeight: "200px" }}
    />
  );
}

/* ===========================================================================
   Upgrade tip (optional, but removes findDOMNode warning completely):
   ---------------------------------------------------------------------------
   ReactQuill v2.0.0+ no longer calls ReactDOM.findDOMNode. If you can, run:
   ---------------------------------------------------------------------------
     npm i react-quill@latest
   ---------------------------------------------------------------------------
   and restart your dev server. Otherwise the existing warning is harmless.
   ========================================================================== */
